import { useEffect, useRef } from 'react';

import { REFERENCE_TYPE_STYLES } from './reference-styles';
import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { ChunkDocumentDrawerContent } from '@/new/components/reference-content/chunk-document-drawer-content';
import { DocumentDrawerContent } from '@/new/components/reference-content/document-drawer-content';
import { FactDocumentDrawerContent } from '@/new/components/reference-content/fact-document-drawer-content';
import { ThreadFileChunkDrawerContent } from '@/new/components/reference-content/thread-file-chunk-drawer-content';
import { ResizableSidePanel } from '@/new/components/resizable-side-panel';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

interface ReferenceSidePanelProps {
  onClose: () => void;
  references: { id: string; index: number; referenceType: ReferenceType }[];
  activeReferenceId: string;
  activeReferenceType: ReferenceType;
  onSelectReference: (id: string, referenceType: ReferenceType) => void;
}

export function ReferenceSidePanel({
  onClose,
  references,
  activeReferenceId,
  activeReferenceType,
  onSelectReference,
}: ReferenceSidePanelProps) {
  const referenceButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const scrollActiveReferenceIntoView = () => {
      referenceButtonRefs.current[activeReferenceId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    };

    const animationFrame = requestAnimationFrame(scrollActiveReferenceIntoView);
    const timeout = window.setTimeout(scrollActiveReferenceIntoView, 220);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.clearTimeout(timeout);
    };
  }, [activeReferenceId, references.length]);

  return (
    <ResizableSidePanel storageKey="kim-reference" onClose={onClose} className="border-l">
      {/* Reference list bar */}
      <div className="border-b p-2.5 py-2 pr-0">
        <div className="flex items-center justify-between gap-2">
          {references.length > 0 && (
            <div className="flex w-full gap-1 overflow-x-auto mask-r-from-95% pr-5 [scrollbar-width:none]">
              {references.map((ref) => (
                <button
                  key={ref.id}
                  ref={(element) => {
                    referenceButtonRefs.current[ref.id] = element;
                  }}
                  onClick={() => onSelectReference(ref.id, ref.referenceType)}
                  className={cn(
                    'shrink-0 rounded-md border px-2 py-0.5 text-xs ring-offset-1 transition-colors duration-150 focus-visible:ring-1 focus-visible:outline-none',
                    REFERENCE_TYPE_STYLES[ref.referenceType].chip,
                    ref.id === activeReferenceId ? '' : 'opacity-50 hover:opacity-80',
                  )}
                >
                  {ref.index}
                </button>
              ))}
            </div>
          )}

          <Button onClick={onClose} variant={'ghost'} size={'iconSm'} className="mr-2">
            <Icon className="lucide--panel-right-close" />
          </Button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {activeReferenceType === ReferenceType.Chunk && <ChunkDocumentDrawerContent chunkId={activeReferenceId} />}
        {activeReferenceType === ReferenceType.Document && <DocumentDrawerContent docId={activeReferenceId} />}
        {activeReferenceType === ReferenceType.Fact && <FactDocumentDrawerContent factId={activeReferenceId} />}
        {activeReferenceType === ReferenceType.ThreadFileChunk && (
          <ThreadFileChunkDrawerContent chunkId={activeReferenceId} />
        )}
        {activeReferenceType === ReferenceType.ThreadFile && (
          <ThreadFileChunkDrawerContent fileId={activeReferenceId} />
        )}
      </div>
    </ResizableSidePanel>
  );
}
